import api from './Api';

/**
 * RealtimeTranscription — connects to OpenAI Realtime API for live transcription.
 * Fetches an ephemeral session token from the backend, opens a WebSocket,
 * and streams audio via AudioWorklet for real-time transcription.
 */
export default class RealtimeTranscription {
    constructor({ onTranscript, onError, language = 'es' }) {
        this.onTranscript = onTranscript;
        this.onError = onError || (() => {});
        this.language = language;
        this.ws = null;
        this.audioContext = null;
        this.workletNode = null;
        this.source = null;
        this.transcript = '';
        this.partialTranscript = '';
        this.stopped = false;
    }

    async start(mediaStream) {
        this.stopped = false;
        try {
            // Fetch ephemeral session token
            const response = await fetch(`${api.baseURL}/ephemeral/session?lang=${this.language}`, {
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to get ephemeral session');
            const session = await response.json();
            const token = session.client_secret?.value;
            if (!token) throw new Error('No client_secret in session response');

            // Open WebSocket to OpenAI Realtime API
            const wsUrl = `wss://api.openai.com/v1/realtime?intent=transcription`;
            this.ws = new WebSocket(wsUrl, [
                'realtime',
                `openai-insecure-api-key.${token}`,
                'openai-beta.realtime-v1',
            ]);

            this.ws.onopen = () => {
                // Start streaming audio
                this._startAudioStream(mediaStream);
            };

            this.ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    this._handleMessage(msg);
                } catch {
                    // ignore parse errors
                }
            };

            this.ws.onerror = (err) => {
                console.warn('RT transcription WS error:', err);
                this.onError(err);
            };

            this.ws.onclose = () => {
                this._cleanup();
            };
        } catch (error) {
            console.warn('RT transcription start failed:', error);
            this.onError(error);
        }
    }

    stop() {
        this.stopped = true;
        this._cleanup();
    }

    _handleMessage(msg) {
        if (this.stopped) return;

        switch (msg.type) {
            case 'conversation.item.input_audio_transcription.delta':
                // Partial transcript delta
                this.partialTranscript += msg.delta || '';
                this.onTranscript(this.transcript + this.partialTranscript, false);
                break;
            case 'conversation.item.input_audio_transcription.completed':
                // Final transcript for this segment
                this.transcript += (msg.transcript || this.partialTranscript) + ' ';
                this.partialTranscript = '';
                this.onTranscript(this.transcript.trim(), true);
                break;
            case 'error':
                console.warn('RT transcription error:', msg.error);
                break;
            default:
                break;
        }
    }

    async _startAudioStream(mediaStream) {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
            this.source = this.audioContext.createMediaStreamSource(mediaStream);

            // Use ScriptProcessor as fallback (AudioWorklet needs HTTPS + module)
            const bufferSize = 4096;
            const processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

            processor.onaudioprocess = (e) => {
                if (this.stopped || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
                const input = e.inputBuffer.getChannelData(0);
                // Convert Float32 to Int16 PCM
                const pcm16 = new Int16Array(input.length);
                for (let i = 0; i < input.length; i++) {
                    const s = Math.max(-1, Math.min(1, input[i]));
                    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                // Base64 encode
                const bytes = new Uint8Array(pcm16.buffer);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) {
                    binary += String.fromCharCode(bytes[i]);
                }
                const base64 = btoa(binary);
                this.ws.send(JSON.stringify({
                    type: 'input_audio_buffer.append',
                    audio: base64,
                }));
            };

            this.source.connect(processor);
            processor.connect(this.audioContext.destination);
            this.workletNode = processor;
        } catch (err) {
            console.warn('Audio stream setup failed:', err);
            this.onError(err);
        }
    }

    _cleanup() {
        // Disconnect source first to stop feeding the processor
        if (this.source) {
            this.source.disconnect();
            this.source = null;
        }
        if (this.workletNode) {
            this.workletNode.disconnect();
            this.workletNode = null;
        }
        if (this.audioContext) {
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
        if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
            this.ws.close();
        }
        this.ws = null;
    }
}
